import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailDelivery, TransferCommonParameters, TransferEmailParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions } from '@app/client/uploader/store/actions';
import { AccountActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';
import { cleanInputText } from '@app/shared/helpers/helpers';
import { Identity } from '@app/shared/models/identity';
import { Transfer } from '@app/shared/models/transfer';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';

@Component({
  selector: 'app-email-form',
  templateUrl: './email-form.component.html',
  styleUrls: ['./email-form.component.scss']
})

export class EmailFormComponent implements OnInit, OnDestroy {

  @Input() identity: Identity;
  @Input() dropbox?: string;

  @Output() emailFormSubmitted: EventEmitter<{
    title: string,
    customUrl: string,
    delivery: EmailDelivery,
  }> = new EventEmitter();

  transfer$: Observable<Transfer>;
  commonParameters$: Observable<TransferCommonParameters>;
  emailParameters$: Observable<TransferEmailParameters>;
  contacts$: Observable<string[]>;

  emailForm: FormGroup;
  submitted = false;
  senderInputDetailsToggled = false;

  subscriptions: Subscription[] = [];

  constructor(
    private readonly router: Router,
    private store$: Store<fromRoot.State & fromUploader.State>,
  ) {
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.commonParameters$ = this.store$.select(fromUploader.getTransferCommonParameters);
    this.emailParameters$ = this.store$.select(fromUploader.getTransferEmailParameters);
    this.contacts$ = this.store$.select(fromRoot.getContacts);

  }

  get f() { return this.emailForm.controls; }

  get senderEmailControls() { return this.emailForm.get('sender.email'); }

  ngOnInit() {
    this.buildEmailForm();
    this.loadFormData();
    this.handleFormChanges();
  }

  onSubmit() {
    this.submitted = true;
    if (this.emailForm.valid) {
      const { title, description, sender, receivers, customUrl } = this.emailForm.value;
      const buildedEmailFormValue = {
        title,
        description: cleanInputText(description),
        delivery: {
          type: 'Email' as const,
          sender,
          receivers,
        },
        customUrl,
      };
      this.emailFormSubmitted.emit(buildedEmailFormValue);
      this.store$.dispatch(AccountActions.SetUserContacts({ userContacts: receivers }));
    }
  }

  requiredEmailList(control: FormControl) {
    const regexpEmail = new RegExp(Constant.regexpEmail); // NEED TO BE MOVED
    let isValid = {};
    if (control?.value?.length >= 1) {
      control.value.forEach(email => {
        if (regexpEmail.test(email) === false) {
          isValid = { status: 'Invalid' };
        }
      });
    } else {
      isValid = { status: 'Invalid' };
    }
    return isValid;
  }

  onSuggestListUpdated(suggestList) {
    this.store$.dispatch(AccountActions.updateUserContacts({ userContacts: suggestList }));
  }

  goBack() {
    this.router.navigateByUrl('');
  }

  toggleSenderInput() {
    this.senderInputDetailsToggled = !this.senderInputDetailsToggled;
  }

  buildEmailForm() {
    this.emailForm = new FormGroup({
      description: new FormControl(''),
      title: new FormControl(''),
      sender: new FormGroup({
        name: new FormControl(''),
        email: new FormControl('', [Validators.required, Validators.pattern(Constant.regexpEmail)]),
      }),
    });

    if (!this.dropbox) {
      this.emailForm.addControl('receivers', new FormControl('', [Validators.required, this.requiredEmailList]));
    }
  }

  loadFormData() {
    this.commonParameters$.pipe(first()).subscribe((params) => {
      this.emailForm.patchValue({ ...params });
    });
    this.emailParameters$.pipe(first()).subscribe((params) => {
      this.emailForm.patchValue({ ...params, ...params.delivery });
      if (params?.delivery?.sender?.name) {
        this.toggleSenderInput();
      }
    });
  }

  handleFormChanges() {
    this.subscriptions.push(
      this.emailForm.valueChanges.subscribe(changes => {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: changes.sender }));
        this.store$.dispatch(PrepareActions.SetEmailTitle({ title: changes.title }));
        this.store$.dispatch(PrepareActions.SetEmailDescription({ description: cleanInputText(changes.description) }));
        if (changes.receivers) {
          this.store$.dispatch(PrepareActions.SetEmailReceivers({ receivers: changes.receivers }));
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
